import { useEffect, useRef, useState } from 'react';
import { Camera, FileText, Trash2, AlertTriangle } from 'lucide-react';
import { comprimirRecibo, tipoAceptado, ReciboDemasiadoGrande } from '../../utils/receiptImage';
import { formatearPeso } from '../../utils/expenses';

interface Props {
  /** El archivo ya preparado para subir, o `null` si no hay ninguno elegido. */
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  /**
   * El rótulo. Por defecto habla del recibo, que es de donde salió el
   * componente (FE-12); `FE-30` lo reutiliza tal cual para la foto de una
   * novedad y solo cambia esta palabra. No se renombró el componente a algo
   * genérico: sería churn en gastos por cero valor.
   */
  label?: string;
  /** Oculta el "(opcional)" del rótulo cuando quien llama ya lo dice a su manera. */
  ocultarOpcional?: boolean;
}

/**
 * FE-12 — elegir la foto del recibo.
 *
 * **Dos entradas, no una, y es deliberado.** `capture="environment"` abre la
 * cámara trasera de un tirón, que es el gesto que se quiere desde la carretera
 * — pero en Chrome Android y en Safari iOS ese atributo **sustituye** al
 * selector: con él puesto no hay forma de elegir una foto que ya se tomó ni un
 * PDF, que es justamente lo que el `accept` promete y lo que pide adjuntar el
 * soporte de un peaje que llegó escaneado. Un solo input tenía que traicionar a
 * uno de los dos, y en el teléfono traicionaba al que el DoD nombra.
 *
 * En escritorio el atributo se ignora y los dos botones abren el explorador:
 * ahí la duplicidad no estorba porque el segundo dice exactamente lo mismo.
 *
 * La compresión se ejecuta **al elegir**, no al enviar, por dos razones: el
 * usuario ve enseguida cuánto va a subir, y un archivo demasiado grande se
 * rechaza antes de que haya llenado el formulario entero. `comprimirRecibo` es
 * idempotente, así que quien suba después puede volver a llamarla sin
 * recodificar.
 */
/** Los dos botones son gemelos: se reparten el ancho y respetan el objetivo táctil de 44 px. */
const ESTILO_BOTON: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
  flex: '1 1 140px', minHeight: '56px', padding: '0.75rem',
  border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)',
  background: 'transparent', color: 'var(--text-secondary)',
  fontSize: '13px',
};

export function ReceiptPicker({ file, onChange, disabled = false, label = 'Foto del recibo', ocultarOpcional = false }: Props) {
  const camaraRef = useRef<HTMLInputElement>(null);
  const archivoRef = useRef<HTMLInputElement>(null);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // La URL de un `createObjectURL` vive hasta que se revoca: sin esto, elegir
  // diez fotos seguidas deja diez blobs retenidos en la pestaña.
  useEffect(() => {
    if (!file || file.type === 'application/pdf') {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const elegir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const elegido = e.target.files?.[0];
    // El input se limpia siempre: sin esto, volver a elegir EL MISMO archivo no
    // dispara `change` y el usuario cree que el clic no hizo nada.
    e.target.value = '';
    if (!elegido) return;

    setError(null);
    if (!tipoAceptado(elegido)) {
      setError('Tiene que ser una imagen (JPG, PNG, HEIC, WEBP) o un PDF.');
      return;
    }

    setPreparando(true);
    try {
      onChange(await comprimirRecibo(elegido));
    } catch (err) {
      onChange(null);
      setError(err instanceof ReciboDemasiadoGrande ? err.message : 'No se pudo preparar el archivo.');
    } finally {
      setPreparando(false);
    }
  };

  return (
    <div>
      <label className="input-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
        {label}
        {!ocultarOpcional && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (opcional)</span>}
      </label>

      {/* Solo imágenes: `capture` con un `accept` que incluya PDF no tiene
          sentido — la cámara no produce PDFs. */}
      <input
        ref={camaraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={elegir}
        hidden
      />
      {/* Sin `capture`: este es el que abre la galería y deja adjuntar un PDF. */}
      <input
        ref={archivoRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={elegir}
        hidden
      />

      {!file && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => camaraRef.current?.click()}
            disabled={disabled || preparando}
            style={{ ...ESTILO_BOTON, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <Camera size={18} />
            {preparando ? 'Preparando…' : 'Tomar foto'}
          </button>
          <button
            type="button"
            onClick={() => archivoRef.current?.click()}
            disabled={disabled || preparando}
            style={{ ...ESTILO_BOTON, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <FileText size={18} />
            Elegir archivo
          </button>
        </div>
      )}

      {file && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.625rem 0.75rem', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface-2, transparent)',
        }}>
          {preview ? (
            <img
              src={preview}
              alt={label}
              style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: 'var(--radius-md)', flexShrink: 0 }}
            />
          ) : (
            <FileText size={28} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
          )}
          <div style={{ flex: '1 1 140px', minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
              {formatearPeso(file.size)}
              {file.type === 'image/jpeg' && ' · comprimida para subir'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); setError(null); }}
            disabled={disabled}
            aria-label={`Quitar: ${label}`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '44px', height: '44px', flexShrink: 0,
              border: 'none', background: 'transparent', color: '#EF4444',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {error && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '12px', color: '#EF4444', display: 'flex', gap: '0.375rem', alignItems: 'flex-start' }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
          {error}
        </p>
      )}
    </div>
  );
}
